import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/layouts/ClientLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { 
  UserCircle, Camera, Mail, Phone, Calendar, MapPin, 
  Loader2, Pencil, Shield, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import ProfileImageUpload from '../../components/ProfileImageUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientProfile = () => {
  const { user, permissions, committees, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    batch_year: '',
    section: '',
    current_location: '',
    occupation: '',
    bio: ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        batch_year: user.batch_year || '',
        section: user.section || '',
        current_location: user.current_location || '',
        occupation: user.occupation || '',
        bio: user.bio || ''
      });
    }
  }, [user]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/auth/profile`, profileData);
      toast.success('Profile updated successfully');
      setEditDialogOpen(false);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  // Permission label mapping
  const permissionLabels = {
    'view_all_payments': 'View All Payments',
    'verify_payments': 'Verify/Reject Payments',
    'post_announcements': 'Post Announcements',
    'upload_documents': 'Upload Documents',
    'manage_programs': 'Manage Event Programs',
    'manage_logistics': 'Manage Tasks & Logistics',
    'manage_attendance': 'Manage Check-ins',
    'all': 'Full Access'
  };

  return (
    <ClientLayout>
      <div className="space-y-6 animate-fade-in" data-testid="client-profile-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-3xl font-bold text-maroon">My Profile</h1>
          <Button 
            variant="outline" 
            onClick={refreshUser}
            className="gap-2"
            data-testid="refresh-profile-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Profile Card */}
        <Card className="border-0 shadow-lg bg-white overflow-hidden">
          <div className="h-32 bg-gradient-to-r from-maroon to-maroon-hover" />
          <CardContent className="-mt-16 relative">
            <div className="flex flex-col md:flex-row items-center md:items-end gap-6">
              <ProfileImageUpload user={user} onUploadComplete={refreshUser} />
              <div className="text-center md:text-left flex-1 pb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {user?.first_name} {user?.last_name}
                </h2>
                <p className="text-gray-500">{user?.email}</p>
                {user?.batch_year && (
                  <Badge className="mt-2 bg-gold text-gray-900">Batch {user.batch_year}</Badge>
                )}
              </div>
              <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-maroon hover:bg-maroon-hover text-white">
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit Profile
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-maroon">Edit Profile</DialogTitle>
                    <DialogDescription>Update your personal information</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>First Name</Label>
                        <Input
                          value={profileData.first_name}
                          onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Last Name</Label>
                        <Input
                          value={profileData.last_name}
                          onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Batch Year</Label>
                        <Input
                          value={profileData.batch_year}
                          onChange={(e) => setProfileData({ ...profileData, batch_year: e.target.value })}
                          placeholder="e.g., 2000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Section</Label>
                        <Input
                          value={profileData.section}
                          onChange={(e) => setProfileData({ ...profileData, section: e.target.value })}
                          placeholder="e.g., Section A"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        placeholder="+63 XXX XXX XXXX"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Current Location</Label>
                      <Input
                        value={profileData.current_location}
                        onChange={(e) => setProfileData({ ...profileData, current_location: e.target.value })}
                        placeholder="City, Country"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Occupation</Label>
                      <Input
                        value={profileData.occupation}
                        onChange={(e) => setProfileData({ ...profileData, occupation: e.target.value })}
                        placeholder="Your current job/profession"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Bio</Label>
                      <Textarea
                        value={profileData.bio}
                        onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                        placeholder="Tell us about yourself..."
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-maroon hover:bg-maroon-hover text-white" disabled={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Save Changes
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Info Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-maroon flex items-center gap-2">
                <UserCircle className="h-5 w-5" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">{user?.email}</span>
              </div>
              {user?.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{user.phone}</span>
                </div>
              )}
              {user?.current_location && (
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{user.current_location}</span>
                </div>
              )}
              {user?.occupation && (
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-600">{user.occupation}</span>
                </div>
              )}
              {user?.bio && (
                <p className="text-sm text-gray-600 mt-4 border-t pt-4">{user.bio}</p>
              )}
            </CardContent>
          </Card>

          {/* Committee Roles & Permissions */}
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-maroon flex items-center gap-2">
                <Shield className="h-5 w-5" />
                My Committee Roles
              </CardTitle>
              <CardDescription>Your assigned committees and permissions</CardDescription>
            </CardHeader>
            <CardContent>
              {committees && committees.length > 0 ? (
                <div className="space-y-4">
                  {committees.map((committee, idx) => (
                    <div 
                      key={idx}
                      className="p-4 rounded-xl bg-gradient-to-r from-gold/10 to-transparent border border-gold/30"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={committee.is_head ? 'bg-gold text-gray-900' : 'bg-maroon/10 text-maroon'}>
                          {committee.is_head ? '★ Head' : 'Member'}
                        </Badge>
                        <span className="font-semibold text-gray-900">{committee.name}</span>
                      </div>
                    </div>
                  ))}
                  
                  {/* Permissions */}
                  {permissions && permissions.length > 0 && (
                    <div className="mt-6 pt-4 border-t">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Your Permissions</h4>
                      <div className="flex flex-wrap gap-2">
                        {permissions.map((perm, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {permissionLabels[perm] || perm}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>You are not assigned to any committee yet</p>
                  <p className="text-sm mt-1">Contact your organizer to join a committee</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </ClientLayout>
  );
};

export default ClientProfile;
